import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getHorizonScanningApi, type UnsubscribeStatus } from "./api";

export function UnsubscribeScreen() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<UnsubscribeStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing unsubscribe token.");
      return;
    }
    let cancelled = false;
    getHorizonScanningApi()
      .getUnsubscribeStatus(token)
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "This link is not valid.");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onUnsubscribe() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const next = await getHorizonScanningApi().unsubscribe({ token });
      setStatus(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not unsubscribe.");
    } finally {
      setBusy(false);
    }
  }

  async function onResume() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const next = await getHorizonScanningApi().unsubscribe({ token, resume: true });
      setStatus(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not resume.");
    } finally {
      setBusy(false);
    }
  }

  if (error && !status) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <h1 className="text-2xl font-bold text-ink">Unsubscribe</h1>
        <p className="mt-3 text-ink-soft">{error}</p>
        <Link className="mt-6 inline-block font-semibold text-brand-700" to="/horizonscanning/start">
          Start a watchlist
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl px-5 py-16">
      <h1 className="text-2xl font-bold text-ink">Unsubscribe</h1>
      {status ? (
        <>
          <p className="mt-3 text-ink-soft">{status.message}</p>
          {status.email ? <p className="mt-2 text-sm text-ink-muted">{status.email}</p> : null}
          <div className="mt-6 flex flex-wrap gap-3">
            {!status.unsubscribed ? (
              <button type="button" className="r1-btn-primary" disabled={busy} onClick={() => void onUnsubscribe()}>
                {busy ? "Saving…" : status.kind === "recipient" ? "Stop sending to me" : "Pause all digests"}
              </button>
            ) : status.kind === "org" ? (
              <button type="button" className="r1-btn-secondary" disabled={busy} onClick={() => void onResume()}>
                {busy ? "Saving…" : "Resume digests"}
              </button>
            ) : (
              <p className="text-sm text-ink-soft">You can close this page.</p>
            )}
          </div>
        </>
      ) : (
        <p className="mt-3 text-ink-soft">Loading…</p>
      )}
      {error && status ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
    </div>
  );
}
